import { createContext, useContext } from 'react';

// Faculty theme configuration based on Eduvos brand guidelines
export interface FacultyTheme {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  foreground: string;
}

export const facultyThemes: Record<string, FacultyTheme> = {
  'law-commerce': {
    name: 'Law & Commerce',
    primary: '#E7D0B0', // Beige tones
    secondary: '#D4BD97',
    accent: '#15305D', // Heritage Blue
    background: '#FDFCFA',
    foreground: '#15305D',
  },
  humanities: {
    name: 'Humanities',
    primary: '#B48DFF', // Purple tones
    secondary: '#9B7AE6',
    accent: '#15305D',
    background: '#FDFBFF',
    foreground: '#15305D',
  },
  'applied-science': {
    name: 'Applied Science',
    primary: '#85BF3F', // Green tones
    secondary: '#CDEF92',
    accent: '#15305D',
    background: '#FBFEFB',
    foreground: '#15305D',
  },
  it: {
    name: 'Information Technology',
    primary: '#214FB1', // Blue tones
    secondary: '#29ABE2',
    accent: '#15305D',
    background: '#FAFCFE',
    foreground: '#15305D',
  },
  default: {
    name: 'Eduvos',
    primary: '#15305D', // Heritage Blue
    secondary: '#29ABE2', // Future Blue
    accent: '#29ABE2',
    background: '#FFFFFF',
    foreground: '#15305D',
  },
};

// Theme context for React components
export const ThemeContext = createContext<{
  currentTheme: FacultyTheme;
  setTheme: (facultyKey: string) => void;
}>({
  currentTheme: facultyThemes.default,
  setTheme: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// CSS custom properties generator for dynamic theming
export const generateThemeCSS = (theme: FacultyTheme): string => {
  return `
    :root {
      --theme-primary: ${theme.primary};
      --theme-secondary: ${theme.secondary};
      --theme-accent: ${theme.accent};
      --theme-background: ${theme.background};
      --theme-foreground: ${theme.foreground};
    }
  `;
};

// Utility function to apply theme to document
export const applyTheme = (facultyKey: string) => {
  const theme = facultyThemes[facultyKey] || facultyThemes.default;
  const css = generateThemeCSS(theme);
  
  // Remove existing theme style if it exists
  const existingStyle = document.getElementById('faculty-theme');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Add new theme style
  const style = document.createElement('style');
  style.id = 'faculty-theme';
  style.textContent = css;
  document.head.appendChild(style);
};

// Strategic pillar colors mapping
export const strategicPillars = {
  scale: {
    color: '#29ABE2', // Future Blue
    name: 'Scale',
    description: 'Growth and expansion across Africa',
  },
  quality: {
    color: '#85BF3F', // Secondary Green
    name: 'Quality',
    description: 'Excellence in education and outcomes',
  },
  culture: {
    color: '#8764E3', // Secondary Purple
    name: 'Culture',
    description: 'Organisational health and engagement',
  },
  impact: {
    color: '#F4A93B', // Secondary Yellow
    name: 'Impact',
    description: 'Meaningful change and transformation',
  },
};

// Brand compliance utilities
export const brandCompliance = {
  // Minimum logo size check (45mm equivalent)
  isLogoSizeCompliant: (width: number, height: number): boolean => {
    const minSize = 45; // 45mm equivalent in pixels (approximate)
    return Math.min(width, height) >= minSize;
  },
  
  // Color contrast validation for accessibility
  getContrastRatio: (_color1: string, _color2: string): number => {
    // Simplified contrast calculation - in production, use a proper library
    // This is a placeholder for brand compliance checking
    return 4.5; // WCAG AA compliance minimum
  },
  
  // Validate color usage against brand guidelines
  isColorCompliant: (color: string): boolean => {
    const approvedColors = [
      '#15305D', '#29ABE2', '#2780D8', '#2554CE', '#214FB1', '#1B4087',
      '#8764E3', '#F5377E', '#E8233C', '#F08C1E', '#F4A93B', '#85BF3F',
      '#E7D0B0', '#B48DFF', '#CDEF92'
    ];
    return approvedColors.includes(color.toUpperCase());
  },
};
